import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, XIcon, ArrowRightIcon, BookOpenIcon, MicIcon, CameraIcon, UploadIcon } from 'lucide-react';
const Hero = () => {
  const [showModal, setShowModal] = useState(false);
  const codeExample = `import { FluidForm } from '@fluidform/react'
const schema = {
  dateOfBirth: { 
    type: 'date', 
    validation: { 
      ai: 'ensure date is reasonable for adult' 
    } 
  }
}
const config = {
  AIChatInput: true,
  documentInput: true,
  voiceInput: true,
  cameraInput: true
}
<FluidForm schema={schema} config={config} />`;
  return <>
      <section className="w-full bg-gradient-to-br from-slate-900 to-slate-800 py-20 relative overflow-hidden" data-id="element-106">
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-40" data-id="element-107"></div>
        <div className="container mx-auto px-4 relative z-10" data-id="element-108">
          <div className="max-w-6xl mx-auto" data-id="element-109">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="space-y-12" data-id="element-110">
              {/* Headlines Section */}
              <div className="space-y-6 text-center" data-id="element-111">
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white" data-id="element-112">
                  Transform any form into an AI-powered experience
                </h1>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold leading-tight text-orange-500" data-id="element-113">
                  with just a few lines of code
                </h2>
                <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto" data-id="element-114">
                  Schema-first conversational forms with built-in AI validation,
                  multi-modal inputs, and intelligent follow-ups.
                </p>
              </div>
              {/* Code Editor and Preview Side by Side */}
              <div className="grid md:grid-cols-2 gap-6" data-id="element-115">
                {/* Code Editor */}
                <div className="relative min-w-0" data-id="element-116">
                  <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg blur opacity-20" data-id="element-117"></div>
                  <div className="relative bg-slate-950 rounded-lg overflow-hidden border border-slate-700" data-id="element-118">
                    {/* Editor Header */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-slate-900 border-b border-slate-700" data-id="element-119">
                      <div className="flex gap-1.5" data-id="element-120">
                        <div className="w-3 h-3 rounded-full bg-slate-600" data-id="element-121"></div>
                        <div className="w-3 h-3 rounded-full bg-slate-600" data-id="element-122"></div>
                        <div className="w-3 h-3 rounded-full bg-slate-600" data-id="element-123"></div>
                      </div>
                      <span className="text-xs text-slate-400 ml-2 font-mono" data-id="element-124">
                        App.tsx
                      </span>
                    </div>
                    {/* Code Content with horizontal scroll */}
                    <div className="overflow-x-auto" data-id="element-125">
                      <pre className="p-6 text-sm font-mono" data-id="element-126">
                        <code className="text-slate-300 whitespace-pre" data-id="element-127">
                          <span className="text-purple-400" data-id="element-128">import</span>{' '}
                          <span className="text-slate-300" data-id="element-129">{'{'}</span>{' '}
                          <span className="text-orange-400" data-id="element-130">FluidForm</span>{' '}
                          <span className="text-slate-300" data-id="element-131">{'}'}</span>{' '}
                          <span className="text-purple-400" data-id="element-132">from</span>{' '}
                          <span className="text-green-400" data-id="element-133">
                            '@fluidform/react'
                          </span>
                          {'\n\n'}
                          <span className="text-purple-400" data-id="element-134">const</span>{' '}
                          <span className="text-blue-400" data-id="element-135">schema</span>{' '}
                          <span className="text-slate-300" data-id="element-136">= {'{'}</span>
                          {'\n  '}
                          <span className="text-blue-300" data-id="element-137">dateOfBirth</span>
                          <span className="text-slate-300" data-id="element-138">:</span>{' '}
                          <span className="text-slate-300" data-id="element-139">{'{'}</span>
                          {'\n    '}
                          <span className="text-blue-300" data-id="element-140">type</span>
                          <span className="text-slate-300" data-id="element-141">:</span>{' '}
                          <span className="text-green-400" data-id="element-142">'date'</span>
                          <span className="text-slate-300" data-id="element-143">,</span>
                          {'\n    '}
                          <span className="text-blue-300" data-id="element-144">validation</span>
                          <span className="text-slate-300" data-id="element-145">:</span>{' '}
                          <span className="text-slate-300" data-id="element-146">{'{'}</span>
                          {'\n      '}
                          <span className="text-blue-300" data-id="element-147">ai</span>
                          <span className="text-slate-300" data-id="element-148">:</span>{' '}
                          <span className="text-green-400" data-id="element-149">
                            'ensure date is reasonable for adult'
                          </span>
                          {'\n    '}
                          <span className="text-slate-300" data-id="element-150">{'}'}</span>
                          {'\n  '}
                          <span className="text-slate-300" data-id="element-151">{'}'}</span>
                          {'\n'}
                          <span className="text-slate-300" data-id="element-152">{'}'}</span>
                          {'\n\n'}
                          <span className="text-purple-400" data-id="element-153">const</span>{' '}
                          <span className="text-blue-400" data-id="element-154">config</span>{' '}
                          <span className="text-slate-300" data-id="element-155">= {'{'}</span>
                          {'\n  '}
                          <span className="text-blue-300" data-id="element-156">AIChatInput</span>
                          <span className="text-slate-300" data-id="element-157">:</span>{' '}
                          <span className="text-orange-400" data-id="element-158">true</span>
                          <span className="text-slate-300" data-id="element-159">,</span>
                          {'\n  '}
                          <span className="text-blue-300" data-id="element-160">documentInput</span>
                          <span className="text-slate-300" data-id="element-161">:</span>{' '}
                          <span className="text-orange-400" data-id="element-162">true</span>
                          <span className="text-slate-300" data-id="element-163">,</span>
                          {'\n  '}
                          <span className="text-blue-300" data-id="element-164">voiceInput</span>
                          <span className="text-slate-300" data-id="element-165">:</span>{' '}
                          <span className="text-orange-400" data-id="element-166">true</span>
                          <span className="text-slate-300" data-id="element-167">,</span>
                          {'\n  '}
                          <span className="text-blue-300" data-id="element-168">cameraInput</span>
                          <span className="text-slate-300" data-id="element-169">:</span>{' '}
                          <span className="text-orange-400" data-id="element-170">true</span>
                          {'\n'}
                          <span className="text-slate-300" data-id="element-171">{'}'}</span>
                          {'\n\n'}
                          <span className="text-slate-300" data-id="element-172">{'<'}</span>
                          <span className="text-orange-400" data-id="element-173">
                            FluidForm
                          </span>{' '}
                          <span className="text-blue-300" data-id="element-174">schema</span>
                          <span className="text-slate-300" data-id="element-175">=</span>
                          <span className="text-slate-300" data-id="element-176">{'{'}</span>
                          <span className="text-blue-400" data-id="element-177">schema</span>
                          <span className="text-slate-300" data-id="element-178">{'}'}</span>{' '}
                          <span className="text-blue-300" data-id="element-179">config</span>
                          <span className="text-slate-300" data-id="element-180">=</span>
                          <span className="text-slate-300" data-id="element-181">{'{'}</span>
                          <span className="text-blue-400" data-id="element-182">config</span>
                          <span className="text-slate-300" data-id="element-183">{'}'}</span>{' '}
                          <span className="text-slate-300" data-id="element-184">{'/>'}</span>
                        </code>
                      </pre>
                    </div>
                  </div>
                </div>
                {/* Live Preview */}
                <div className="relative min-w-0" data-id="element-185">
                  <div className="absolute -inset-1 bg-gradient-to-r from-slate-700 to-slate-600 rounded-lg blur opacity-20" data-id="element-186"></div>
                  <div className="relative bg-white rounded-lg overflow-hidden border border-slate-300 shadow-xl" data-id="element-187">
                    {/* Browser Chrome */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-slate-100 border-b border-slate-200" data-id="element-188">
                      <div className="flex gap-1.5" data-id="element-189">
                        <div className="w-3 h-3 rounded-full bg-red-400" data-id="element-190"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400" data-id="element-191"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400" data-id="element-192"></div>
                      </div>
                      <span className="text-xs text-slate-500 ml-2 truncate" data-id="element-193">
                        localhost:3000
                      </span>
                    </div>
                    {/* Form Preview - Scrollable Container */}
                    <div className="h-[400px] overflow-y-auto overflow-x-hidden bg-white" data-id="element-194">
                      <div className="p-4 sm:p-6 flex gap-4 sm:gap-8" data-id="element-195">
                        {/* Form Fields */}
                        <div className="flex-1 space-y-4 min-w-0" data-id="element-196">
                          {/* Previous fields (partially visible) */}
                          <div className="space-y-2 opacity-40" data-id="element-197">
                            <label className="text-sm font-medium text-slate-700" data-id="element-198">
                              Full Name
                            </label>
                            <input type="text" value="John Smith" readOnly className="w-full h-10 bg-slate-50 rounded border border-slate-300 px-3 text-sm text-slate-600" data-id="element-199" />
                          </div>
                          <div className="space-y-2 opacity-40" data-id="element-200">
                            <label className="text-sm font-medium text-slate-700" data-id="element-201">
                              Email
                            </label>
                            <input type="text" value="john@example.com" readOnly className="w-full h-10 bg-slate-50 rounded border border-slate-300 px-3 text-sm text-slate-600" data-id="element-202" />
                          </div>
                          {/* Current focused field */}
                          <div className="space-y-2" data-id="element-203">
                            <label className="text-sm font-medium text-slate-700" data-id="element-204">
                              Date of Birth
                            </label>
                            <input type="text" value="17 September 2024" readOnly className="w-full h-10 bg-slate-50 rounded border border-orange-300 px-3 text-sm text-slate-600" data-id="element-205" />
                            <div className="flex items-center gap-2 text-xs text-orange-600" data-id="element-206">
                              <SparklesIcon className="w-3 h-3 flex-shrink-0" data-id="element-207" />
                              <span data-id="element-208">Date seems too recent, please verify</span>
                            </div>
                          </div>
                          {/* More fields below (partially visible) */}
                          <div className="space-y-2 opacity-40" data-id="element-209">
                            <label className="text-sm font-medium text-slate-700" data-id="element-210">
                              Address
                            </label>
                            <input type="text" value="" readOnly className="w-full h-10 bg-slate-50 rounded border border-slate-300 px-3 text-sm text-slate-600" data-id="element-211" />
                          </div>
                          <div className="space-y-2 opacity-40" data-id="element-212">
                            <label className="text-sm font-medium text-slate-700" data-id="element-213">
                              Phone Number
                            </label>
                            <input type="text" value="" readOnly className="w-full h-10 bg-slate-50 rounded border border-slate-300 px-3 text-sm text-slate-600" data-id="element-214" />
                          </div>
                          <div className="space-y-2 opacity-40" data-id="element-215">
                            <label className="text-sm font-medium text-slate-700" data-id="element-216">
                              Occupation
                            </label>
                            <input type="text" value="" readOnly className="w-full h-10 bg-slate-50 rounded border border-slate-300 px-3 text-sm text-slate-600" data-id="element-217" />
                          </div>
                          <button className="w-full h-10 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded font-medium opacity-40" data-id="element-218">
                            Submit
                          </button>
                        </div>
                        {/* Multi-Modal Buttons - Positioned Right with Active Mic */}
                        <div className="flex flex-col gap-3 justify-center pr-2 pl-2 sm:pl-4 flex-shrink-0" data-id="element-219">
                          <button className="w-10 h-10 bg-orange-500 hover:bg-orange-600 rounded-full flex items-center justify-center transition-colors shadow-lg relative" data-id="element-220">
                            <MicIcon className="w-5 h-5 text-white" data-id="element-221" />
                            {/* Active pulse animation */}
                            <span className="absolute inset-0 rounded-full bg-orange-500 animate-ping opacity-75" data-id="element-222"></span>
                          </button>
                          <button className="w-10 h-10 bg-slate-900 hover:bg-slate-800 rounded-full flex items-center justify-center transition-colors shadow-md" data-id="element-223">
                            <UploadIcon className="w-5 h-5 text-white" data-id="element-224" />
                          </button>
                          <button className="w-10 h-10 bg-slate-900 hover:bg-slate-800 rounded-full flex items-center justify-center transition-colors shadow-md" data-id="element-225">
                            <CameraIcon className="w-5 h-5 text-white" data-id="element-226" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-227">
                <a href="https://cal.com/fluidform/30min" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center gap-3 group" data-id="element-228">
                  Schedule a Demo
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-229" />
                </a>
                <a href="https://accounts.fluid-form.ai/waitlist" target="_blank" rel="noopener noreferrer" className="px-8 py-4 bg-slate-800 text-white rounded-lg text-lg font-semibold hover:bg-slate-700 transition-all duration-300 border border-slate-600 flex items-center gap-3" data-id="element-230">
                  <BookOpenIcon className="w-5 h-5" data-id="element-231" />
                  View API Docs
                </a>
              </div>
              <p className="text-sm text-slate-400 text-center" data-id="element-232">
                Free tier: 1,000 requests/month • No credit card required
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Modal */}
      <AnimatePresence data-id="element-233">
        {showModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowModal(false)} data-id="element-234">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden" data-id="element-235">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-236">
                <h2 className="text-2xl font-bold text-gray-800" data-id="element-237">
                  Schedule a Demo
                </h2>
                <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-238">
                  <XIcon className="w-6 h-6" data-id="element-239" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto" style={{
            maxHeight: 'calc(90vh - 80px)'
          }} data-id="element-240">
                <iframe src="https://app.fluid-form.ai/ff/fluidform-landing-page-form" width="100%" height="600px" title="FluidForm Builder" frameBorder="0" className="rounded-lg" data-id="element-241" />
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
};
export default Hero;